// ─── What: the time axis and the probability axis ─────────────────────────────

import * as d3 from 'd3';
import { drawPercentAxis, drawAxisFitButton } from '../../chartAxis';
import type { AxisFit } from '../../chartYDomain';
import { MARGIN } from '../layout';
import type { DrawCtx } from '../drawContext';

/** Tick density and label format both follow the visible span. */
export function xTickFormat(xDomain: [Date, Date]): {
    tickCount: number;
    fmt: (d: Date) => string;
    spanDays: number;
} {
    const spanMs = xDomain[1].getTime() - xDomain[0].getTime();
    const spanMin = spanMs / 60_000;
    const spanHours = spanMin / 60;
    const spanDays = spanHours / 24;

    const tickCount = spanHours <= 48 ? 6 : spanDays <= 90 ? 7 : 5;
    const fmt: (d: Date) => string =
        spanMin <= 30
            ? d3.timeFormat('%H:%M:%S')
            : spanHours <= 12
              ? d3.timeFormat('%H:%M')
              : spanDays <= 7
                ? d3.timeFormat('%b %d %H:%M')
                : spanDays <= 365
                  ? d3.timeFormat('%b %d')
                  : d3.timeFormat("%b '%y");

    return { tickCount, fmt, spanDays };
}

export function drawXAxis(ctx: DrawCtx): void {
    const { svg, xScale, xDomain, colors, layout } = ctx;
    const { tickCount, fmt } = xTickFormat(xDomain);
    const xTicks = xScale.ticks(tickCount);

    svg.append('g')
        .selectAll('text')
        .data(xTicks)
        .join('text')
        .attr('x', d => xScale(d))
        .attr('y', MARGIN.top - 6)
        .attr('fill', colors.axisTextColor)
        .attr('font-size', '10px')
        .attr('font-weight', '500')
        .attr('font-family', 'monospace')
        .attr('text-anchor', 'middle')
        .text(d => fmt(d));

    svg.append('g')
        .selectAll('line')
        .data(xTicks)
        .join('line')
        .attr('x1', d => xScale(d))
        .attr('x2', d => xScale(d))
        .attr('y1', MARGIN.top)
        .attr('y2', layout.probHeight)
        .attr('stroke', colors.gridColor)
        .attr('stroke-width', 0.5);
}

export function drawProbabilityAxis(
    ctx: DrawCtx,
    fit: AxisFit,
    toggleFit: () => void
): void {
    const { svg, yScale, probDomain, colors, layout, theme, chartType } = ctx;

    drawPercentAxis(svg, {
        ticks: probDomain.ticks,
        y: v => yScale(v),
        x1: MARGIN.left,
        x2: MARGIN.left + layout.w,
        labelSide: 'right',
        gridColor: colors.gridColor,
        textColor: colors.textColor,
        fontSize: '10px',
        decimals: probDomain.decimals,
    });

    // Stacked is a 100% composition — there is nothing to fit it to.
    if (chartType !== 'stacked') {
        drawAxisFitButton(svg, {
            x: MARGIN.left + layout.w + 4,
            y: MARGIN.top - 26,
            fit,
            accentColor: theme.palette.primary.main,
            mutedColor: colors.textColor,
            onToggle: toggleFit,
        });
    }
}
